/**
 * @krisspy-file
 * @type page
 * @name "BookingsList"
 * @title "Bookings List"
 * @description "View and manage all rental bookings"
 * @routes ["/bookings"]
 * @layout "DashboardLayout"
 * @flowName "booking-management"
 * @design "reference"
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  X,
  Download,
  Calendar,
  MapPin,
  DollarSign,
  User,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Clock,
  PlayCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Truck,
  StickyNote
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getBookings,
  getBookingCategoryCounts,
  type BookingWithItems,
  type BookingItemWithDetails,
  type BookingCategoryCounts,
} from '../services/bookings.service';
import { categoryFromBookingState, type BookingCategory } from '../lib/bookingCategories';
import { getLocations } from '../services/locations.service';
import { getMerchantById } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { initScrollFix, cleanupScrollFix } from '../lib/scrollFix';
import { formatCurrency } from '../lib/currency';
import { utcIsoToZonedIsoWithOffset } from '../lib/timezone';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useUrlFilters } from '../lib/useUrlFilters';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type Location = Database['public']['Tables']['locations']['Row'];

/**
 * Check if a booking contains items whose home location differs from the booking location.
 * This indicates an inter-location transfer is required before the rental.
 * Checks both the booking item's product type location AND the assigned inventory item's
 * product type location (an inventory item may belong to a product type at a different location).
 */
function hasTransferItems(booking: BookingWithItems): boolean {
  if (!booking.items || !booking.location_id) return false;
  return booking.items.some(item =>
    (item.product_types?.location_id && item.product_types.location_id !== booking.location_id) ||
    (item.inventory_items?.product_types?.location_id && item.inventory_items.product_types.location_id !== booking.location_id)
  );
}

/**
 * Format booking items into two short lines for the list/card views.
 *
 * Line 1: Products grouped by name with sizes (e.g., "2x Mountain Bike (S, M), 1x Road Bike (L)")
 * Line 2: Add-ons with quantities (e.g., "+ 1x Helmet, 2x Lock")
 *
 * Truncation is count-based: up to `maxProducts` grouped products and
 * `maxAddons` grouped addons are shown, with "+N more" appended when the
 * grouping exceeds the cap.
 */
function formatBookingItems(
  items: BookingItemWithDetails[] | undefined,
  { maxProducts = 4, maxAddons = 3 }: { maxProducts?: number; maxAddons?: number } = {}
): { products: string; addons: string } {
  if (!items || items.length === 0) {
    return { products: '', addons: '' };
  }

  // Separate products from add-ons
  const products: BookingItemWithDetails[] = [];
  const addons: BookingItemWithDetails[] = [];

  items.forEach(item => {
    if (item.product_type === 'addon') {
      addons.push(item);
    } else {
      products.push(item);
    }
  });

  // Group products by name and collect sizes
  const productGroups = new Map<string, { count: number; sizes: Set<string> }>();
  products.forEach(item => {
    const name = item.product_name || 'Unknown Product';
    const existing = productGroups.get(name) || { count: 0, sizes: new Set<string>() };
    existing.count += item.quantity || 1;
    if (item.size) {
      existing.sizes.add(item.size);
    }
    productGroups.set(name, existing);
  });

  // Format products line: cap by number of grouped items
  const productEntries = Array.from(productGroups.entries());
  const shownProducts = productEntries.slice(0, maxProducts);
  const remainingProducts = productEntries.length - shownProducts.length;

  const productParts = shownProducts.map(([name, data]) => {
    const sizePart = data.sizes.size > 0 ? ` (${Array.from(data.sizes).join(', ')})` : '';
    return `${data.count}x ${name}${sizePart}`;
  });

  let productsStr = productParts.join(', ');
  if (remainingProducts > 0) {
    productsStr += `, +${remainingProducts} more`;
  }

  // Group add-ons by name
  const addonGroups = new Map<string, number>();
  addons.forEach(item => {
    const name = item.product_name || 'Unknown Add-on';
    addonGroups.set(name, (addonGroups.get(name) || 0) + (item.quantity || 1));
  });

  // Format add-ons line: cap by number of grouped items
  const addonEntries = Array.from(addonGroups.entries());
  const shownAddons = addonEntries.slice(0, maxAddons);
  const remainingAddons = addonEntries.length - shownAddons.length;

  const addonParts = shownAddons.map(([name, count]) => `${count}x ${name}`);

  let addonsStr = addonParts.length > 0 ? '+ ' + addonParts.join(', ') : '';
  if (remainingAddons > 0) {
    addonsStr += `, +${remainingAddons} more`;
  }

  return { products: productsStr, addons: addonsStr };
}

/**
 * Calculate and format duration between two dates.
 * Returns a human-readable string like "2h 30m", "1d 4h", "3d"
 */
function formatDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) return '0m';

  const totalMinutes = Math.round(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`);

  return parts.join(' ') || '0m';
}

export default function BookingsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin, hasRole } = useAuth();
  const { formatDateTimeCompact, formatDateLong, timezone } = useDateFormatter();
  const canEdit = hasRole('operator');

  // Use URL-based filters for persistence across navigation
  const { getFilter, setFilter, clearFilters: clearUrlFilters } = useUrlFilters({
    defaults: {
      search: '',
      category: 'upcoming',
      payment: 'all',
      location: 'all',
      startDate: '',
      endDate: '',
      transferOnly: '',
      deliveryOnly: '',
      inventoryItem: ''
    }
  });

  // Derive filter values from URL
  const searchQuery = getFilter('search');
  const category = (getFilter('category') || 'upcoming') as BookingCategory;
  const paymentStatusFilter = getFilter('payment') || 'all';
  const locationFilter = getFilter('location') || 'all';
  const dateRangeStart = getFilter('startDate');
  const dateRangeEnd = getFilter('endDate');
  const transferOnly = getFilter('transferOnly') === 'true';
  const deliveryOnly = getFilter('deliveryOnly') === 'true';
  const inventoryItemFilter = getFilter('inventoryItem');

  // Setter functions that update URL
  const setSearchQuery = (value: string) => setFilter('search', value);
  const setCategory = (value: BookingCategory) => setFilter('category', value);
  const setPaymentStatusFilter = (value: string) => setFilter('payment', value);
  const setLocationFilter = (value: string) => setFilter('location', value);
  const setDateRangeStart = (value: string) => setFilter('startDate', value);
  const setDateRangeEnd = (value: string) => setFilter('endDate', value);
  const setTransferOnly = (value: boolean) => setFilter('transferOnly', value ? 'true' : '');
  const setDeliveryOnly = (value: boolean) => setFilter('deliveryOnly', value ? 'true' : '');
  const clearInventoryItemFilter = () => setFilter('inventoryItem', '');

  // Human-readable label for the inventory item filter chip
  const [inventoryItemLabel, setInventoryItemLabel] = useState<string | null>(null);

  // Real data state
  const [bookings, setBookings] = useState<BookingWithItems[]>([]);
  // Per-tab counts. The list query is scoped to the selected category, so the
  // tab badges and the results total come from a separate lightweight query
  // rather than from the loaded rows.
  const [categoryCounts, setCategoryCounts] = useState<BookingCategoryCounts>({
    upcoming: 0,
    active: 0,
    returned: 0,
    cancelled: 0,
    all: 0,
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Drop stale responses when filters change faster than the network
  const loadRequestIdRef = useRef(0);

  // Load bookings and locations on mount and when filters change
  useEffect(() => {
    loadData();
  }, [category, paymentStatusFilter, locationFilter, dateRangeStart, dateRangeEnd, inventoryItemFilter]);

  // Fetch a label for the inventory-item filter chip when the URL param is set
  useEffect(() => {
    if (!inventoryItemFilter) {
      setInventoryItemLabel(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('inventory_items')
        .select('item_number, nr, product_types(product_name)')
        .eq('id', inventoryItemFilter)
        .maybeSingle();
      if (cancelled) return;
      if (!data) {
        setInventoryItemLabel(inventoryItemFilter);
        return;
      }
      const tag = (data as any).item_number || ((data as any).nr ? `#${(data as any).nr}` : null);
      const product = (data as any).product_types?.product_name;
      setInventoryItemLabel([product, tag].filter(Boolean).join(' • ') || inventoryItemFilter);
    })();
    return () => {
      cancelled = true;
    };
  }, [inventoryItemFilter]);

  // Initialize scroll fix for tables
  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollFix();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanupScrollFix();
    };
  }, [bookings]);

  async function loadData() {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError(null);

    // Load merchant currency
    if (merchantId) {
      const { data: merchant } = await getMerchantById(merchantId, 'en');
      if (requestId !== loadRequestIdRef.current) return;
      if (merchant?.currency_code) {
        setCurrency(merchant.currency_code);
      }
    }

    // Load locations with access control
    const { data: locationsData, error: locationsError } = await getLocations(
      { merchantId, locationIds, isPlatformAdmin },
      { active: true }
    );
    if (requestId !== loadRequestIdRef.current) return;
    if (locationsError) {
      setError('Failed to load locations');
    } else if (locationsData) {
      setLocations(locationsData);
    }

    // Load the selected category only. Filtering server-side keeps the default
    // view to the handful of live bookings instead of pulling the merchant's
    // entire history — the historical tabs grow without bound.
    const sharedFilters = {
      locationId: locationFilter !== 'all' ? locationFilter : undefined,
      paymentState: paymentStatusFilter !== 'all' ? paymentStatusFilter : undefined,
      startDate: dateRangeStart || undefined,
      endDate: dateRangeEnd || undefined,
      searchQuery: searchQuery || undefined,
      inventoryItemId: inventoryItemFilter || undefined,
    };

    const [
      { data: bookingsData, error: bookingsError },
      { data: countsData, error: countsError },
    ] = await Promise.all([
      getBookings(
        { merchantId, locationIds, isPlatformAdmin },
        { ...sharedFilters, category }
      ),
      getBookingCategoryCounts({ merchantId, locationIds, isPlatformAdmin }, sharedFilters),
    ]);
    if (requestId !== loadRequestIdRef.current) return;

    if (bookingsError) {
      setError(bookingsError);
    } else if (bookingsData) {
      setBookings(bookingsData);
    }

    // A counts failure must not blank the list, but it must not pass unnoticed
    // either — stale tab badges are exactly the kind of quiet wrongness this
    // change exists to remove.
    if (countsData) {
      setCategoryCounts(countsData);
    } else if (countsError) {
      console.error('[BookingsList] Failed to load category counts:', countsError);
    }

    setLoading(false);
  }

  // Category configuration
  const categories = [
    { id: 'upcoming' as BookingCategory, label: t('bookings.upcoming'), icon: Clock, color: 'blue' },
    { id: 'active' as BookingCategory, label: t('common.active'), icon: PlayCircle, color: 'green' },
    { id: 'returned' as BookingCategory, label: t('bookings.returned'), icon: CheckCircle, color: 'gray' },
    { id: 'cancelled' as BookingCategory, label: t('status.cancelled'), icon: XCircle, color: 'red' },
    { id: 'all' as BookingCategory, label: t('common.all'), icon: Filter, color: 'gray' },
  ];

  const paymentStatuses = ['all', 'open', 'open_pay_later', 'partially_paid', 'paid', 'refunded', 'partially_refunded'];

  // Categorize bookings by their state. Shared with the server-side tab filter
  // so the two cannot drift apart.
  const categorizeBooking = (booking: BookingWithItems): BookingCategory =>
    categoryFromBookingState(booking.booking_state);

  // Filter bookings by transfer, delivery and search filters. The category is
  // already applied server-side; re-checking it here is a cheap consistency
  // guard that costs nothing once the rows are loaded.
  const categorizedBookings = bookings.filter((booking) => {
    // Category filter
    if (category !== 'all') {
      const bookingCategory = categorizeBooking(booking);
      if (bookingCategory !== category) return false;
    }

    // Transfer / Delivery filters (OR logic: show bookings matching either condition)
    if (transferOnly || deliveryOnly) {
      const matchesTransfer = transferOnly && hasTransferItems(booking);
      const matchesDelivery = deliveryOnly && booking.is_delivery_location;
      if (!matchesTransfer && !matchesDelivery) return false;
    }

    // Search filter
    if (searchQuery) {
      const customerName = `${booking.customer_firstname} ${booking.customer_lastname}`.toLowerCase();
      const search = searchQuery.toLowerCase();
      return (
        customerName.includes(search) ||
        booking.order_id.toLowerCase().includes(search) ||
        booking.customer_email.toLowerCase().includes(search)
      );
    }

    return true;
  });

  // Sort bookings: upcoming by start date, active by return date (next return first),
  // returned by actual return time (most recent first, falling back to end_date),
  // others by created date.
  const sortedBookings = [...categorizedBookings].sort((a, b) => {
    if (category === 'active') {
      return new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
    }
    if (category === 'upcoming') {
      return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    }
    if (category === 'returned') {
      const aReturned = new Date(a.returned_at || a.end_date).getTime();
      const bReturned = new Date(b.returned_at || b.end_date).getTime();
      return bReturned - aReturned;
    }
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  // Group bookings by date (for upcoming and active categories)
  const groupBookingsByDate = (bookings: BookingWithItems[], dateField: 'start_date' | 'end_date' = 'start_date') => {
    const groups: { date: string; bookings: BookingWithItems[] }[] = [];
    const dateMap = new Map<string, BookingWithItems[]>();

    bookings.forEach(booking => {
      const dateStr = formatDateLong(booking[dateField]);

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, []);
      }
      dateMap.get(dateStr)!.push(booking);
    });

    dateMap.forEach((bookings, date) => {
      groups.push({ date, bookings });
    });

    return groups;
  };

  const useCardView = category === 'upcoming' || category === 'active';
  const groupedBookings = useCardView
    ? groupBookingsByDate(sortedBookings, category === 'active' ? 'end_date' : 'start_date')
    : [];

  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const formatProductsForExport = (items: BookingItemWithDetails[] | undefined): string => {
    if (!items || items.length === 0) return '';
    const parts: string[] = [];
    const groups = new Map<string, { count: number; sizes: Set<string> }>();
    for (const item of items) {
      if (item.product_type === 'addon') continue;
      const name = item.product_name || 'Unknown';
      const g = groups.get(name) || { count: 0, sizes: new Set<string>() };
      g.count += item.quantity || 1;
      if (item.size) g.sizes.add(item.size);
      groups.set(name, g);
    }
    for (const [name, data] of groups) {
      const sizePart = data.sizes.size > 0 ? ` (${Array.from(data.sizes).join(', ')})` : '';
      parts.push(`${data.count}x ${name}${sizePart}`);
    }
    // Add add-ons
    const addonGroups = new Map<string, number>();
    for (const item of items) {
      if (item.product_type !== 'addon') continue;
      const name = item.product_name || 'Unknown';
      addonGroups.set(name, (addonGroups.get(name) || 0) + (item.quantity || 1));
    }
    for (const [name, count] of addonGroups) {
      parts.push(`${count}x ${name} [add-on]`);
    }
    return parts.join(', ');
  };

  const exportToCSV = () => {
    const dataToExport = useCardView
      ? groupedBookings.flatMap(g => g.bookings)
      : sortedBookings;

    if (dataToExport.length === 0) return;

    const headers = [
      'Order ID',
      'Booking State',
      'Payment State',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Location',
      'Start Date',
      'End Date',
      'Duration',
      'Products',
      'Qty',
      'Subtotal',
      'Tax',
      'Discount',
      'Discount Code',
      'Total',
      'Amount Paid',
      'Currency',
      'Delivery Address',
      'Internal Notes',
      'Custom Fields',
      'Created At',
    ];

    const rows = dataToExport.map((b) => {
      const deliveryAddr = [b.delivery_street, b.delivery_zip, b.delivery_city, b.delivery_country]
        .filter(Boolean)
        .join(', ');

      const customFields = b.custom_field_values && typeof b.custom_field_values === 'object'
        ? Object.entries(b.custom_field_values as Record<string, string>)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ')
        : '';

      return [
        b.order_id || '',
        b.booking_state || '',
        b.payment_state || '',
        b.customer_firstname || '',
        b.customer_lastname || '',
        b.customer_email || '',
        b.customer_phone || '',
        b.location_name || '',
        // Merchant-local ISO 8601 with explicit offset (e.g. 2026-11-30T06:00:00+01:00),
        // matching the admin UI and the Analytics export instead of raw UTC.
        utcIsoToZonedIsoWithOffset(b.start_date, timezone),
        utcIsoToZonedIsoWithOffset(b.end_date, timezone),
        formatDuration(b.start_date, b.end_date),
        formatProductsForExport(b.items),
        (b.items_count || 0).toString(),
        (b.subtotal ?? 0).toFixed(2),
        (b.total_tax ?? 0).toFixed(2),
        (b.total_discount ?? 0).toFixed(2),
        b.discount_code_used || '',
        (b.total_amount ?? 0).toFixed(2),
        (b.amount_paid ?? 0).toFixed(2),
        b.currency || '',
        deliveryAddr,
        b.internal_notes || '',
        customFields,
        utcIsoToZonedIsoWithOffset(b.created_at, timezone),
      ];
    });

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return 'badge-info';
      case 'active':
        return 'badge-success';
      case 'returned':
        return 'badge-success';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'badge-success';
      case 'open':
        return 'badge-warning';
      case 'open_pay_later':
        return 'badge-warning';
      case 'partially_paid':
        return 'badge-warning';
      case 'refunded':
        return 'badge-info';
      case 'partially_refunded':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  const clearFilters = () => {
    clearUrlFilters();
  };

  // Get count for each category (from the dedicated counts query — the loaded
  // rows only cover the selected tab)
  const getCategoryCount = (cat: BookingCategory) => categoryCounts[cat];

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="page-header" data-testid="bookings-page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('bookings.title')}</h1>
          <p className="text-secondary">{t('bookings.description')}</p>
        </div>
        <div className="page-header-actions" data-testid="bookings-page-header-actions">
          <button
            onClick={exportToCSV}
            className="btn btn-ghost"
            disabled={loading || sortedBookings.length === 0}
          >
            <Download className="w-4 h-4" />
            {t('actions.exportReport')}
          </button>
          {canEdit && (
          <button
            onClick={() => navigate('/bookings/create')}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            {t('bookings.newBooking')}
          </button>
          )}
        </div>
      </div>

      {/* Category Tabs with Content */}
      <div className="card p-0 overflow-hidden">
        {/* Tab Navigation */}
        <div className="flex border-b border-primary overflow-x-auto" data-testid="bookings-category-tabs">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = category === cat.id;
            const count = getCategoryCount(cat.id);

            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                className={`flex items-center gap-sm px-lg py-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-blue border-b-2 border-blue bg-blue/5'
                    : 'text-secondary hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{cat.label}</span>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  isActive
                    ? 'bg-blue text-white'
                    : 'bg-secondary text-secondary'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-lg">
          {/* Active filter chip: bookings filtered to a specific inventory item */}
          {inventoryItemFilter && (
            <div className="mb-lg flex items-center gap-sm flex-wrap">
              <span className="text-xs text-secondary">{t('bookings.filteredBy')}</span>
              <span className="inline-flex items-center gap-xs px-md py-xs bg-blue/10 text-blue rounded-full text-sm font-medium">
                {t('bookings.inventoryItemChipLabel')}: {inventoryItemLabel || '…'}
                <button
                  type="button"
                  onClick={clearInventoryItemFilter}
                  className="hover:text-blue/70"
                  aria-label={t('common.clear')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          )}

          {/* Filters Section */}
          <div className="space-y-md mb-lg">
            {/* Row 1: Search + Location (always visible) */}
            <div className="flex flex-col lg:flex-row gap-md">
              {/* Search Bar */}
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
                  <input
                    type="text"
                    placeholder={t('bookings.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input pl-10 w-full"
                  />
                </div>
              </div>

              {/* Location Filter */}
              <div className="w-full lg:w-48 flex-shrink-0">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="input pl-10 w-full appearance-none truncate"
                  >
                    <option key="all" value="all">{t('products.allLocations')}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.location_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Mobile toggle for additional filters */}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="flex items-center gap-xs text-sm font-medium text-blue lg:hidden"
            >
              <Filter className="w-4 h-4" />
              {mobileFiltersOpen ? t('bookings.hideFilters') : t('bookings.moreFilters')}
              {mobileFiltersOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {/* Collapsible filters: hidden on mobile by default, always visible on lg+ */}
            <div className={`space-y-md ${mobileFiltersOpen ? '' : 'hidden lg:block'}`}>
              {/* Wraps instead of squeezing: date inputs keep a minimum width and the
                  buttons move to a second row when the row is too narrow (e.g. tablets) */}
              <div className="flex flex-col lg:flex-row flex-wrap lg:items-end gap-md">
                {/* Payment Status Filter */}
                <div className="w-full lg:w-48 min-w-0">
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('bookings.paymentStatus')}
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <select
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      className="input pl-10 w-full appearance-none"
                    >
                      {paymentStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status === 'all' ? t('bookings.allPayments') : status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Date Range Start */}
                <div className="flex-1 min-w-[11rem]">
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('bookings.startDateFrom')}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="date"
                      value={dateRangeStart}
                      onChange={(e) => setDateRangeStart(e.target.value)}
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Date Range End */}
                <div className="flex-1 min-w-[11rem]">
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('bookings.endDateTo')}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="date"
                      value={dateRangeEnd}
                      onChange={(e) => setDateRangeEnd(e.target.value)}
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Transfer Required Filter */}
                <div>
                  <button
                    type="button"
                    onClick={() => setTransferOnly(!transferOnly)}
                    className={`btn ${transferOnly ? 'btn-primary' : 'btn-secondary'} flex items-center gap-xs whitespace-nowrap w-full`}
                    title="Show only bookings with items from another location"
                  >
                    <Truck className="w-4 h-4" />
                    {t('bookings.transferRequired')}
                  </button>
                </div>

                {/* Delivery Required Filter */}
                <div>
                  <button
                    type="button"
                    onClick={() => setDeliveryOnly(!deliveryOnly)}
                    className={`btn ${deliveryOnly ? 'btn-primary' : 'btn-secondary'} flex items-center gap-xs whitespace-nowrap w-full`}
                    title="Show only bookings at delivery-enabled locations"
                  >
                    <Truck className="w-4 h-4" />
                    {t('bookings.deliveryRequired')}
                  </button>
                </div>

                {/* Clear Filters Button */}
                <div>
                  <button
                    onClick={clearFilters}
                    className="btn btn-ghost whitespace-nowrap w-full"
                  >
                    <X className="w-4 h-4" />
                    {t('bookings.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <div className="flex items-start gap-md">
                <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-danger mb-xs">{t('bookings.errorLoading')}</h4>
                  <p className="text-sm text-secondary">{error}</p>
                  <button onClick={loadData} className="btn btn-sm btn-primary mt-md">
                    {t('common.retry')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bookings Display */}
          <div className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-blue animate-spin" />
            <span className="ml-md text-secondary">{t('bookings.loadingBookings')}</span>
          </div>
        ) : useCardView && groupedBookings.length > 0 ? (
          /* Grouped card view for upcoming and active bookings */
          <div className="divide-y divide-border">
            {groupedBookings.map((group, groupIdx) => (
              <div key={groupIdx} className="p-lg">
                <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-md">
                  <Calendar className="w-5 h-5 text-blue" />
                  {category === 'active' ? `Returns: ${group.date}` : group.date}
                  <span className="text-sm font-normal text-secondary">
                    ({group.bookings.length} {group.bookings.length === 1 ? 'booking' : 'bookings'})
                  </span>
                </h3>
                <div className="space-y-md">
                  {group.bookings.map((booking) => (
                    <div
                      key={booking.id}
                      onClick={() => navigate(`/bookings/${booking.id}/edit`)}
                      className="border border-border rounded-lg p-md hover:border-blue hover:bg-secondary transition-colors cursor-pointer"
                    >
                      <div className="flex flex-wrap items-center gap-x-lg gap-y-xs mb-sm">
                        <span className="font-medium text-primary">{booking.order_id}</span>
                        {(booking as any).is_test && (
                          <span
                            className="ml-xs px-xs py-0.5 rounded bg-warning/10 text-warning border border-warning/30 text-xs font-medium align-middle"
                            title={t('bookings.testBookingHint')}
                          >
                            {t('bookings.testBooking')}
                          </span>
                        )}
                        {booking.internal_notes && booking.internal_notes.trim() && (
                          <span
                            title={`${t('bookings.hasInternalNotes')}: ${booking.internal_notes.slice(0, 80)}${booking.internal_notes.length > 80 ? '…' : ''}`}
                            aria-label={t('bookings.hasInternalNotes')}
                            className="inline-flex"
                          >
                            <StickyNote className="w-4 h-4 text-warning flex-shrink-0" />
                          </span>
                        )}
                        {booking.booking_state === 'cancelled' && Number(booking.amount_paid || 0) > 0 && (!booking.refund_history || booking.refund_history.length === 0) && (
                          <span
                            title={t('bookings.cancelledNoRefund')}
                            aria-label={t('bookings.cancelledNoRefund')}
                            className="inline-flex"
                          >
                            <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
                          </span>
                        )}
                        <span className={`badge ${getPaymentStatusBadge(booking.payment_state)}`}>
                          {booking.payment_state.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </span>
                        {(booking.delivery_city || booking.delivery_street) ? (
                          <span className="flex items-center gap-xs text-sm text-blue min-w-0">
                            <Truck className="w-4 h-4 flex-shrink-0" />
                            <span className="break-words">{booking.delivery_city || booking.delivery_area_name || 'Delivery'}</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-xs text-sm text-secondary min-w-0">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="break-words">{booking.location_name}</span>
                          </span>
                        )}
                        {hasTransferItems(booking) && (
                          <span className="flex items-center gap-xs text-sm text-danger min-w-0" title="Contains items from another location">
                            <Truck className="w-4 h-4 flex-shrink-0" />
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-md text-sm">
                        <div className="flex items-center gap-xs text-secondary">
                          <User className="w-4 h-4" />
                          <span>{booking.customer_firstname} {booking.customer_lastname}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-xs text-secondary min-w-0">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {formatDateTimeCompact(booking.start_date)} - {formatDateTimeCompact(booking.end_date)}
                          </span>
                          <span className="text-xs text-secondary/70">
                            ({formatDuration(booking.start_date, booking.end_date)})
                          </span>
                        </div>
                        <div className="flex items-center gap-xs text-secondary">
                          <span className="font-medium text-primary">
                            {(booking.total_amount ?? 0).toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                      {booking.items && booking.items.length > 0 && (
                        <div className="mt-sm text-sm text-secondary">
                          {(() => {
                            const { products, addons } = formatBookingItems(booking.items, { maxProducts: 5 });
                            return (
                              <>
                                {products && <div>{products}</div>}
                                {addons && <div className="text-xs text-secondary/70">{addons}</div>}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : sortedBookings.length > 0 ? (
          /* Table view for other categories */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-primary">
                <tr>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.bookingId')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.customer')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.location')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.product')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.quantity')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.start')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.end')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.duration')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.status')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.payment')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedBookings.map((booking) => (
                  <tr
                    key={booking.id}
                    onClick={() => navigate(`/bookings/${booking.id}/edit`)}
                    className="hover:bg-secondary transition-colors cursor-pointer"
                  >
                    <td className="py-md px-lg">
                      <div className="flex items-center gap-xs">
                        <span className="text-sm font-medium text-primary">{booking.order_id}</span>
                        {(booking as any).is_test && (
                          <span
                            className="ml-xs px-xs py-0.5 rounded bg-warning/10 text-warning border border-warning/30 text-xs font-medium align-middle"
                            title={t('bookings.testBookingHint')}
                          >
                            {t('bookings.testBooking')}
                          </span>
                        )}
                        {booking.internal_notes && booking.internal_notes.trim() && (
                          <span
                            title={`${t('bookings.hasInternalNotes')}: ${booking.internal_notes.slice(0, 80)}${booking.internal_notes.length > 80 ? '…' : ''}`}
                            aria-label={t('bookings.hasInternalNotes')}
                            className="inline-flex"
                          >
                            <StickyNote className="w-4 h-4 text-warning flex-shrink-0" />
                          </span>
                        )}
                        {booking.booking_state === 'cancelled' && Number(booking.amount_paid || 0) > 0 && (!booking.refund_history || booking.refund_history.length === 0) && (
                          <span
                            title={t('bookings.cancelledNoRefund')}
                            aria-label={t('bookings.cancelledNoRefund')}
                            className="inline-flex"
                          >
                            <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-md px-lg">
                      <div className="flex items-center gap-xs">
                        <User className="w-4 h-4 text-secondary" />
                        <div>
                          <p className="text-sm font-medium text-primary">
                            {booking.customer_firstname} {booking.customer_lastname}
                          </p>
                          <p className="text-xs text-secondary">{booking.customer_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-md px-lg">
                      <div className="flex items-center gap-xs">
                        {(booking.delivery_city || booking.delivery_street) ? (
                          <>
                            <Truck className="w-4 h-4 text-blue flex-shrink-0" />
                            <div>
                              <p className="text-sm text-primary">{booking.delivery_city || booking.delivery_area_name || 'Delivery'}</p>
                              {booking.delivery_street && (
                                <p className="text-xs text-secondary truncate max-w-[160px]" title={booking.delivery_street}>{booking.delivery_street}</p>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                            <span className="text-sm text-secondary">{booking.location_name}</span>
                          </>
                        )}
                        {hasTransferItems(booking) && (
                          <span title="Contains items from another location"><Truck className="w-4 h-4 text-danger flex-shrink-0" /></span>
                        )}
                      </div>
                    </td>
                    <td className="py-md px-lg max-w-xs">
                      {booking.items && booking.items.length > 0 ? (
                        <div className="text-sm">
                          {(() => {
                            const { products, addons } = formatBookingItems(booking.items);
                            return (
                              <>
                                <div className="text-secondary line-clamp-2" title={products}>{products || 'No products'}</div>
                                {addons && <div className="text-xs text-secondary/70 truncate" title={addons}>{addons}</div>}
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <span className="text-sm text-secondary">No items</span>
                      )}
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm text-primary font-medium">{booking.items_count || 0}</span>
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm text-secondary whitespace-nowrap">
                        {formatDateTimeCompact(booking.start_date)}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      {booking.booking_state === 'returned' && booking.returned_at ? (
                        <div className="whitespace-nowrap">
                          <div className="flex items-center gap-xs text-sm text-primary">
                            <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                            <span>{formatDateTimeCompact(booking.returned_at)}</span>
                          </div>
                          <div className="text-xs text-tertiary mt-0.5">
                            {t('bookings.scheduled')}: {formatDateTimeCompact(booking.end_date)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-secondary whitespace-nowrap">
                          {formatDateTimeCompact(booking.end_date)}
                        </span>
                      )}
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm text-secondary whitespace-nowrap">
                        {formatDuration(booking.start_date, booking.end_date)}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <span className={`badge ${getStatusBadge(booking.booking_state)}`}>
                        {booking.booking_state.charAt(0).toUpperCase() + booking.booking_state.slice(1)}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <span className={`badge ${getPaymentStatusBadge(booking.payment_state)}`}>
                        {booking.payment_state.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm font-medium text-primary">
                        {formatCurrency(booking.total_amount, currency)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
              <Calendar className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('bookings.noBookingsFound')}</h3>
            <p className="text-secondary text-sm mb-xl">
              {t('bookings.tryAdjustingFilters')}
            </p>
            <button
              onClick={() => navigate('/bookings/create')}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4" />
              {t('bookings.newBooking')}
            </button>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {(sortedBookings.length > 0 || groupedBookings.length > 0) && (
        <div className="flex items-center justify-between text-sm text-secondary">
          <p>
            {t('common.showing')} <span className="font-medium text-primary">
              {category === 'upcoming' ? groupedBookings.reduce((sum, g) => sum + g.bookings.length, 0) : sortedBookings.length}
            </span> {t('common.of')}{' '}
            <span className="font-medium text-primary">{categoryCounts.all}</span> {t('bookings.bookings')}
          </p>
          <p>
            {t('bookings.totalRevenue')}: <span className="font-medium text-success">
              {formatCurrency(
                category === 'upcoming'
                  ? groupedBookings.reduce((sum, g) => sum + g.bookings.reduce((s, b) => s + (b.total_amount || 0), 0), 0)
                  : sortedBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0),
                currency
              )}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
